import { DiscordSDK, patchUrlMappings } from "@discord/embedded-app-sdk";
import { traceAuthStep, traceAuthStepWithRetry, SERVER_AUTH_STEP_TIMEOUT_MS } from "./authTrace.js";

// The browser↔Discord handshake (`sdk.ready()`) is the fragile step — it can
// stall transiently (dropped WebSocket / momentary rate-limit). Retry it a few
// times with a fresh SDK instance before giving up, using a shorter per-attempt
// timeout so all attempts still fail fast enough to show the user a hint.
const SDK_READY_ATTEMPTS = 3;
const SDK_READY_TIMEOUT_MS = 12_000;

export interface ActivityAuth {
  accessToken: string;
  userId: string;
  username: string;
}

let sdk: DiscordSDK | null = null;

export async function initActivity(
  appPath: string,
  clientId: string,
  backendBaseUrl: string,
): Promise<ActivityAuth> {
  if (sdk !== null) throw new Error("initActivity called more than once — SDK already initialised");

  // Build the SDK and complete the handshake with retry. Each attempt gets a
  // fresh DiscordSDK so a wedged instance can't poison the retry. `sdk` is only
  // assigned once an attempt succeeds.
  const activeSdk = await traceAuthStepWithRetry(
    "sdk-ready",
    async () => {
      const candidate = new DiscordSDK(clientId);
      await candidate.ready();
      return candidate;
    },
    { attempts: SDK_READY_ATTEMPTS, timeoutMs: SDK_READY_TIMEOUT_MS },
  );
  sdk = activeSdk;

  patchUrlMappings([{ prefix: "/api", target: `${new URL(backendBaseUrl).host}/api` }]);

  const { code } = await traceAuthStep("authorize", () =>
    activeSdk.commands.authorize({
      client_id: clientId,
      response_type: "code",
      state: "",
      prompt: "none",
      scope: ["identify", "guilds.members.read"],
    }),
  );

  const { access_token } = await traceAuthStep("token-exchange", async () => {
    const tokenRes = await fetch(`${appPath}/auth/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!tokenRes.ok) throw new Error(`Token exchange failed: ${tokenRes.status}`);
    return (await tokenRes.json()) as { access_token: string };
  }, SERVER_AUTH_STEP_TIMEOUT_MS);

  const authResult = await traceAuthStep("authenticate", () =>
    activeSdk.commands.authenticate({ access_token }),
  );
  if (!authResult.user?.id || !authResult.user?.username) {
    throw new Error("authenticate succeeded but returned incomplete user data");
  }

  return {
    accessToken: access_token,
    userId: authResult.user.id,
    username: authResult.user.username,
  };
}

export function getSDK(): DiscordSDK | null {
  return sdk;
}
